import { Component, h } from 'preact'
import Icon from '../assets'
import Content from '../common/Content'
import EventHeader from './EventHeader'
import EventAttendees, { UserData } from './EventAttendees'
import globals from '../../utils/globals'
import axios from 'axios'
import tracker from '../../components/tracker/publish'
import EventDateFormatter from './EventDateFormatter'
import Phrase from '../../libs/translationsUtils/phrase'
import FeaturedTopicsListAsSearchResults from './FeaturedTopicsListAsSearchResults'

interface Props {
  id: number
  title: string
  content: string
  startAt: number
  endAt: number
  isOpenForSignUp: boolean
  isLoggedInUserAttendee: boolean
  attendeeCount: number
  attendees: UserData[]
  type?: string
  image?: string
  location?: string
  link?: string
  linkLabel?: string
  confirmationMessage?: string
}

interface Group {
  id: number
  title: string
  thumbnailImage?: string
  isPrivate: boolean
}

interface DataProps {
  event: Props
  group?: Group
  attachmentCdn: string
}

interface Details {
  scheduleAt: string
  location?: string
  link?: string
  linkLabel?: string
  group?: Group
  attachmentCdn: string
  googleCalendarLink: string
  isPastEvent: boolean
}

interface Description {
  content: string
}

interface State {
  isAttendee: boolean
  isLoading?: boolean
  showModalEditRsvp: boolean
  showModalAttendConfirmation: boolean
  rsvpStatus: boolean
  page: number
  isAttendeesLoading: boolean
  attendeeCount: number
  attendees: UserData[]
  isLoadingConfirm: boolean
  isLoadingUpdate: boolean
  isLoadingAttend: boolean
}

class EventView extends Component<DataProps, State> {
  isEventEngagementEnabled: boolean
  scheduleAt: string
  scheduleAtDetailView: string
  constructor (props: DataProps) {
    super(props)
    const { isLoggedInUserAttendee, attendeeCount, attendees, startAt, endAt } = { ...props.event }

    this.state = {
      isAttendee: isLoggedInUserAttendee,
      isLoading: false,
      showModalEditRsvp: false,
      showModalAttendConfirmation: false,
      rsvpStatus: isLoggedInUserAttendee,
      page: 1,
      isAttendeesLoading: false,
      attendeeCount: attendeeCount,
      attendees: attendees,
      isLoadingConfirm: false,
      isLoadingUpdate: false,
      isLoadingAttend: false
    }
    this.isEventEngagementEnabled = ((globals('activeModules') || []).includes('eventEngagement'))
    this.scheduleAt = EventDateFormatter.formatDateForEventView(startAt, endAt, false)
    this.scheduleAtDetailView = EventDateFormatter.formatDateForEventView(startAt, endAt, true)
    this.onAttend = this.onAttend.bind(this)
    this.onEditRsvp = this.onEditRsvp.bind(this)
    this.onCloseModalAttendConfirmation = this.onCloseModalAttendConfirmation.bind(this)
    this.onCloseModalEditRsvp = this.onCloseModalEditRsvp.bind(this)
    this.loadNextPage = this.loadNextPage.bind(this)
  }

  onAttendanceChanged (isAttending: boolean): void {
    this.setState({ rsvpStatus: isAttending })
  }

  onEditRsvp () {
    this.setState({ showModalEditRsvp: true })
  }

  onCloseModalEditRsvp () {
    this.setState({ showModalEditRsvp: false })
  }

  async onUpdateRsvp (e: Event) {
    e.preventDefault()
    if (this.state.rsvpStatus) {
      this.setState({ showModalEditRsvp: false })
      return
    }
    this.setState({ isLoadingUpdate: true })
    try {
      const res = await axios.post('/event/cancelSignUp', {
        eventId: this.props.event.id
      })
      this.setState({
        isAttendee: false,
        showModalEditRsvp: false,
        isLoadingAttend: false,
        attendees: res.data,
        attendeeCount: this.state.attendeeCount - 1,
        isAttendeesLoading: false,
        isLoadingUpdate: false
      })
    } catch (e) {
      this.setState({ isAttendee: false, isLoadingAttend: false, isAttendeesLoading: false, isLoadingUpdate: false })
    }
  }

  onCloseModalAttendConfirmation () {
    this.setState({
      showModalAttendConfirmation: false,
      isLoadingAttend: false,
      isLoadingConfirm: false
    })
  }

  async onAttend () {
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }
    this.setState({ isLoadingAttend: true })

    try {
      const res = await axios.post('/event/signUp', {
        eventId: this.props.event.id
      })

      this.setState({
        isAttendee: true,
        rsvpStatus: true,
        attendees: res.data,
        attendeeCount: this.state.attendeeCount + 1,
        isAttendeesLoading: false,
        showModalAttendConfirmation: true
      })

      const { userid, name } = window.inSidedData.user
      const eventId = this.props.event.id.toString()
      tracker.trackEvent('userevent', {
        type: 'Event RSVPed',
        community_id: globals('communityId'),
        destination_event_url: window.location.href,
        author_id: userid,
        author_name: name,
        event_id: eventId,
        event_title: this.props.event.title
      })
    } catch (e) {
      this.setState({
        isAttendee: false,
        isLoadingAttend: false,
        isAttendeesLoading: false,
        isLoadingConfirm: false,
        showModalAttendConfirmation: false
      })
    }
  }

  async loadNextPage () {
    this.setState({ isLoading: true })
    try {
      const res = await axios.get(`/events/${this.props.event.id}/attendees?page=${this.state.page + 1}`)
      this.setState({ page: this.state.page + 1, attendees: this.state.attendees.concat(res.data) })
    } catch (e) {}

    this.setState({ isLoading: false })
  }

  createGoogleCalendarLink () {
    const baseLink = 'https://www.google.com/calendar/event?action=TEMPLATE'
    const { title, location, startAt, endAt } = this.props.event
    const formattedTitle = encodeURIComponent(title)
    const formattedLocation = location?.replace(' ', '+') ?? ''
    const formattedStartAt = EventDateFormatter.formatDateForGoogleCalenderLink(startAt)
    const formattedEndAt = EventDateFormatter.formatDateForGoogleCalenderLink(endAt)

    return `${baseLink}&text=${formattedTitle}&details=${window.location.href}&location=${formattedLocation}&dates=${formattedStartAt}/${formattedEndAt}`
  }

  render ({ event, group, attachmentCdn }: DataProps, { isAttendee, showModalEditRsvp, rsvpStatus, isAttendeesLoading, page, attendees, attendeeCount, showModalAttendConfirmation, isLoadingAttend, isLoadingConfirm, isLoadingUpdate }: State) {
    const {
      id,
      title,
      type,
      startAt,
      endAt,
      image,
      location,
      link,
      linkLabel,
      content,
      isOpenForSignUp,
      isLoggedInUserAttendee,
      confirmationMessage
    } = event
    const isPastEvent = EventDateFormatter.isPastEvent(endAt)
    return (
      <div>
        <EventHeader
          eventId={id}
          title={title}
          scheduleAt={this.scheduleAt}
          startAt={startAt}
          endAt={endAt}
          isOpenForSignUp={isOpenForSignUp}
          isLoggedInUserAttendee={isLoggedInUserAttendee}
          image={image}
          type={type}
          confirmationMessage={confirmationMessage}
          isEventEngagementEnabled={this.isEventEngagementEnabled}
          isAttendee={isAttendee}
          isLoadingAttend={isLoadingAttend}
          showModalEditRsvp={showModalEditRsvp}
          showModalAttendConfirmation={showModalAttendConfirmation}
          onCloseModalAttendConfirmation={this.onCloseModalAttendConfirmation}
          rsvpStatus={rsvpStatus}
          onAttend={this.onAttend}
          onEditRsvp={this.onEditRsvp}
          onCloseModalEditRsvp={this.onCloseModalEditRsvp}
          onAttendanceChanged={(value: any) => this.onAttendanceChanged(value)}
          onUpdateRsvp={async (e: Event) => await this.onUpdateRsvp(e)}
          isLoadingConfirm={isLoadingConfirm}
          isLoadingUpdate={isLoadingUpdate}
          eventContent={content}
        />
        <div className='event-body-container'>
          <div className='event-content-wrapper'>
            <EventContent content={content} />
            <EventAttendees
              isPastEvent={isPastEvent}
              eventId={id}
              attendees={attendees}
              attendeeCount={attendeeCount}
              page={page}
              isEventEngagementEnabled={this.isEventEngagementEnabled}
              isLoading={isAttendeesLoading}
              loadNextPage={this.loadNextPage}
            />
            <FeaturedTopicsListAsSearchResults eventId={id} attachmentCdn={attachmentCdn} />
          </div>
          <EventDetails
            scheduleAt={this.scheduleAtDetailView}
            location={location}
            link={link}
            linkLabel={linkLabel}
            group={group}
            attachmentCdn={attachmentCdn}
            googleCalendarLink={this.createGoogleCalendarLink()}
            isPastEvent={isPastEvent}
          />
        </div>
      </div>
    )
  }
}

const EventDetails = ({ scheduleAt, location, link, linkLabel, group, attachmentCdn, googleCalendarLink, isPastEvent }: Details) => {
  const groupThumbImage = group?.thumbnailImage ? `${attachmentCdn}${group?.thumbnailImage}` : null
  const groupTypeLabel = group?.isPrivate ? <Phrase p='events.private.group' m='Forum' /> : null
  return (
    <div className='event-details-wrapper'>
      {group?.isPrivate && <div className='event-details-container'>
        <a class='event-group-url' href={`/groups/${group?.id}`}>
          <div className='event-group-detail-row'>
            {groupThumbImage && <div className='event-group-thumbnail'>
              <div className='event-group-thumbnail__image' style={{ backgroundImage: `url(${groupThumbImage})` }} />
            </div>}
            <div className='event-group-details'>
              <div className='event-group__title'>{group?.title}</div>
              <div className='event-group__type'>{groupTypeLabel}</div>
            </div>
          </div>
        </a>
      </div>}
      <div className='event-details-container'>
        <div className='event-details__header_label'><Phrase p='events.details' m='Forum' /></div>
        {location
          ? <div className='event-detail-row'>
            <div className='event-detail-icon'>
              <Icon icon='people' />
            </div>
            <div className='event-detail-info'>
              <div className='event-detail__label'><Phrase p='events.location.in.person' m='Forum' /></div>
            </div>
          </div>
          : <div className='event-detail-row'>
            <div className='event-detail-icon'>
              <Icon icon='computer' />
            </div>
            <div className='event-detail-info'>
              <div className='event-detail__label'><Phrase p='events.location.online' m='Forum' /></div>
            </div>
          </div>}
        <div className='event-detail-row'>
          <div className='event-detail-icon'>
            <Icon icon='clock' />
          </div>
          <div className='event-detail-info'>
            <div className='event-detail__label'><Phrase p='events.datetime' m='Forum' /></div>
            <div className='event-detail__text'>{scheduleAt}</div>
            {!isPastEvent && <a className='event-detail__google_calendar_link' target='_blank' rel='noreferrer' href={googleCalendarLink}><Phrase p='events.add.to.google.calendar' m='Forum' /></a>}
          </div>
        </div>
        {location &&
          <div className='event-detail-row'>
            <div className='event-detail-icon'>
              <Icon icon='location' />
            </div>
            <div className='event-detail-info'>
              <div className='event-detail__label'><Phrase p='events.location' m='Forum' /></div>
              <div>{location}</div>
            </div>
          </div>}
        {link &&
          <div className='event-detail-row'>
            <div className='event-detail-icon'>
              <Icon icon='link' />
            </div>
            <div className='event-detail-info'>
              <div className='event-detail__label'><Phrase p='events.link' m='Forum' /></div>
              <a className='event-detail__link event-detail__text' href={link} target='_blank' rel='noreferrer'>{linkLabel}</a>
            </div>
          </div>}
      </div>
    </div>
  )
}

const EventContent = ({ content }: Description) => {
  return (
    <div className='event-content-container'>
      <div className='event-content__header_label'><Phrase p='events.about' m='Forum' /></div>
      <Content content={content} className='event__content' />
    </div>
  )
}

export default EventView
